import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Phone, Mail, MessageCircle, X, User } from "lucide-react";
import MOShell, { useMOAccess, isWritable, LoadingBlock, NoAccessScreen } from "@/components/masterOrder/MOShell";
import { listClients, createClient } from "@/lib/masterOrderApi";
import { formatApiError } from "@/lib/api";

/* ------------------------------ Create Drawer ---------------------------- */
function CreateClientDrawer({ open, onClose, onCreated }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", phone: "", email: "", messenger: "", preferred_contact_channel: "",
    default_address: "", source: "", restrictions: "", notes: "", tagsCsv: "",
  });
  const [advanced, setAdvanced] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ name: "", phone: "", email: "", messenger: "", preferred_contact_channel: "",
                default_address: "", source: "", restrictions: "", notes: "", tagsCsv: "" });
      setAdvanced(false);
    }
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  if (!open) return null;

  const submit = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Имя и телефон обязательны");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        messenger: form.messenger.trim() || null,
        preferred_contact_channel: form.preferred_contact_channel.trim() || null,
        default_address: form.default_address.trim() || null,
        source: form.source.trim() || null,
        restrictions: form.restrictions.trim() || null,
        notes: form.notes.trim() || null,
        tags: form.tagsCsv.split(",").map((s) => s.trim()).filter(Boolean),
      };
      const created = await createClient(payload);
      toast.success("Клиент добавлен");
      onCreated(created);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" style={{ background: "rgba(35,23,16,0.42)", backdropFilter: "blur(3px)" }}
         onClick={onClose} data-testid="mo-client-drawer">
      <div className="w-full max-w-md h-full bg-white flex flex-col" onClick={(e) => e.stopPropagation()}
           style={{ boxShadow: "-24px 0 40px -24px rgba(35,23,16,0.4)" }}>
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: "#EFE4D0", background: "linear-gradient(90deg, #F5EBD8, #FBF5EA)" }}>
          <div>
            <div className="text-[10.5px] tracking-[0.24em] uppercase" style={{ color: "#8B6F4B" }}>Клиент</div>
            <div className="text-[20px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>Новый клиент</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full grid place-items-center hover:bg-white/60" style={{ color: "#4A3728" }} aria-label="Закрыть">
            <X size={16} strokeWidth={1.8} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-3">
          <Field label="Имя *"><Input value={form.name} onChange={(v) => set("name", v)} testid="mo-client-name" placeholder="Например: Анна Ковальчук" /></Field>
          <Field label="Телефон *"><Input value={form.phone} onChange={(v) => set("phone", v)} testid="mo-client-phone" placeholder="+..." /></Field>
          <Field label="Email"><Input type="email" value={form.email} onChange={(v) => set("email", v)} testid="mo-client-email" /></Field>
          <Field label="Мессенджер"><Input value={form.messenger} onChange={(v) => set("messenger", v)} testid="mo-client-messenger" placeholder="Telegram / WhatsApp / IG" /></Field>
          <Field label="Ограничения">
            <textarea value={form.restrictions} onChange={(e) => set("restrictions", e.target.value)}
                      className="w-full h-20 p-3 rounded-lg text-[13px] outline-none resize-none"
                      style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                      placeholder="Например: без яиц, без алкоголя, без красного декора"
                      data-testid="mo-client-restrictions" />
            <div className="mt-1 text-[11px] leading-snug" style={{ color: "#9A5F45" }}>
              Не указывайте диагнозы, сведения о здоровье и иную медицинскую информацию.
            </div>
          </Field>

          <Field label="Заметка">
            <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)}
                      className="w-full h-20 p-3 rounded-lg text-[13px] outline-none resize-none"
                      style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                      data-testid="mo-client-notes" />
          </Field>

          <button type="button" onClick={() => setAdvanced((v) => !v)}
                  className="text-[12px] mt-1"
                  style={{ color: "#8B6F4B" }} data-testid="mo-client-advanced-toggle">
            {advanced ? "Скрыть дополнительно" : "Дополнительно →"}
          </button>

          {advanced && (
            <div className="space-y-3 pt-1">
              <Field label="Предпочитаемая связь"><Input value={form.preferred_contact_channel} onChange={(v) => set("preferred_contact_channel", v)} placeholder="Telegram / звонок / email" testid="mo-client-pref" /></Field>
              <Field label="Адрес по умолчанию"><Input value={form.default_address} onChange={(v) => set("default_address", v)} testid="mo-client-address" /></Field>
              <Field label="Источник"><Input value={form.source} onChange={(v) => set("source", v)} testid="mo-client-source" placeholder="Инстаграм, рекомендация..." /></Field>
              <Field label="Теги (через запятую)"><Input value={form.tagsCsv} onChange={(v) => set("tagsCsv", v)} testid="mo-client-tags" placeholder="VIP, свадьбы" /></Field>
            </div>
          )}
        </div>

        <div className="border-t px-5 py-3 flex justify-end gap-2" style={{ borderColor: "#EFE4D0", background: "#FBF5EA" }}>
          <button onClick={onClose} className="h-10 px-4 rounded-xl text-[12.5px]"
                  style={{ background: "#FFFFFF", color: "#4A3728", boxShadow: "inset 0 0 0 1px #D9C6A6" }}>
            Отменить
          </button>
          <button onClick={submit} disabled={saving} className="h-10 px-5 rounded-xl text-[12.5px] disabled:opacity-60"
                  style={{ background: "#4A3728", color: "#F7EFE0" }} data-testid="mo-client-save">
            {saving ? "Сохраняем..." : "Сохранить"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10.5px] tracking-[0.2em] uppercase" style={{ color: "#8B6F4B" }}>{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
export function Input({ value, onChange, testid, type = "text", placeholder, disabled = false }) {
  return (
    <input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className="w-full h-10 px-3 rounded-lg text-[13px] outline-none disabled:opacity-60"
      style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
      data-testid={testid}
    />
  );
}

/* --------------------------------- Page --------------------------------- */
function ClientsList({ items, onOpen }) {
  if (!items.length) {
    return (
      <div className="rounded-xl bg-white p-10 text-center"
           style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }} data-testid="mo-clients-empty">
        <div className="mx-auto w-12 h-12 rounded-full grid place-items-center" style={{ background: "#F5EBD8", color: "#8B6F4B" }}>
          <User size={20} strokeWidth={1.6} />
        </div>
        <div className="mt-3 text-[18px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
          Пока нет клиентов
        </div>
        <p className="text-[12.5px] mt-1.5" style={{ color: "#8B6F4B" }}>
          Добавьте первого клиента — потом он появится в подборе при создании заказа.
        </p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="mo-clients-list">
      {items.map((c) => (
        <button key={c.id} onClick={() => onOpen(c.id)}
                className="text-left rounded-xl bg-white p-4 transition hover:-translate-y-0.5"
                style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                data-testid={`mo-client-row-${c.id}`}>
          <div className="text-[10.5px] tracking-[0.22em] uppercase" style={{ color: "#8B6F4B" }}>Клиент</div>
          <div className="mt-0.5 text-[17px] leading-snug truncate" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
            {c.name}
          </div>
          <div className="mt-2 space-y-1 text-[12px]" style={{ color: "#4A3728" }}>
            <div className="flex items-center gap-1.5"><Phone size={12} strokeWidth={1.6} />{c.phone}</div>
            {c.email && <div className="flex items-center gap-1.5 truncate"><Mail size={12} strokeWidth={1.6} /><span className="truncate">{c.email}</span></div>}
            {c.messenger && <div className="flex items-center gap-1.5 truncate"><MessageCircle size={12} strokeWidth={1.6} /><span className="truncate">{c.messenger}</span></div>}
          </div>
          {c.tags?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {c.tags.slice(0, 5).map((t) => (
                <span key={t} className="text-[10.5px] px-2 py-0.5 rounded-full" style={{ background: "#F5EBD8", color: "#5B4326" }}>{t}</span>
              ))}
            </div>
          )}
        </button>
      ))}
    </div>
  );
}

export default function MOClients() {
  const nav = useNavigate();
  const { mode, loading: aLoad, refetch } = useMOAccess();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [drawer, setDrawer] = useState(false);
  const [sp] = useSearchParams();

  const canWrite = isWritable(mode);

  const load = async () => {
    setLoading(true);
    try { setItems(await listClients()); }
    catch (e) { /* handled below by no_access */ }
    finally { setLoading(false); }
  };
  useEffect(() => { if (!aLoad && mode !== "no_access" && mode !== "demo") load(); /* eslint-disable-line */ }, [aLoad, mode]);
  useEffect(() => { if (sp.get("new") === "1" && canWrite) setDrawer(true); /* eslint-disable-line */ }, [sp, canWrite]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((c) =>
      c.name?.toLowerCase().includes(q) ||
      c.phone?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q) ||
      c.messenger?.toLowerCase().includes(q));
  }, [items, query]);

  return (
    <MOShell active="clients" title="Клиенты" subtitle="Ваши контакты">
      {aLoad ? (
        <LoadingBlock />
      ) : mode === "no_access" || mode === "demo" ? (
        <NoAccessScreen />
      ) : (
        <>
          <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
            <div className="flex-1 flex items-center gap-2 h-10 px-4 rounded-xl bg-white"
                 style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
              <Search size={14} strokeWidth={1.7} color="#8B6F4B" />
              <input className="flex-1 bg-transparent outline-none text-[13px] placeholder:text-[#B9A692]"
                     placeholder="Поиск: имя, телефон, email, мессенджер..."
                     value={query} onChange={(e) => setQuery(e.target.value)}
                     data-testid="mo-clients-search" />
              <span className="text-[11px] tabular-nums" style={{ color: "#8B6F4B" }}>{filtered.length}</span>
            </div>
            {canWrite && (
              <button onClick={() => setDrawer(true)}
                      className="h-10 px-5 rounded-xl text-[13px] inline-flex items-center gap-2"
                      style={{ background: "#4A3728", color: "#F7EFE0" }}
                      data-testid="mo-clients-add">
                <Plus size={15} strokeWidth={1.8} /> Добавить клиента
              </button>
            )}
          </div>

          {loading ? <LoadingBlock /> : <ClientsList items={filtered} onOpen={(id) => nav(`/master-order/clients/${id}`)} />}

          <CreateClientDrawer
            open={drawer}
            onClose={() => setDrawer(false)}
            onCreated={(c) => {
              setDrawer(false);
              setItems((cur) => [c, ...cur]);
              refetch();
              nav(`/master-order/clients/${c.id}`);
            }}
          />
        </>
      )}
    </MOShell>
  );
}
